import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useStore } from '@hub/helpers/use-store';
import { formatDate } from '@monorepo/client-common';
import { Icons } from '@monorepo/icons';
import {
  Card,
  Col,
  Flex,
  Row,
  Statistic,
  TextComponent,
  Tooltip,
} from '@monorepo/react-components';
import {
  LoadingState,
  useLoading,
  useResponsiveness,
} from '@monorepo/react-hooks';
import { IconsNames } from '@monorepo/types';
import { AccountStats } from '@monorepo/types';

import './best-rates-cards.scss';

interface Props {
  accountId: string;
}

const BestRatesCards: FunctionComponent<Props> = ({ accountId }) => {
  const { isMobile } = useResponsiveness();
  const {
    dataStore: { accountStore },
  } = useStore();
  const { t } = useTranslation(['dashboardTemplates', 'common']);
  const { loadingState, updateLoadingState } = useLoading();
  const [accountStats, setAccountStats] = useState<AccountStats>({
    riskRewardRatio: 0,
    successRate: 0,
  });

  useEffect(() => {
    const init = async () => {
      try {
        updateLoadingState(LoadingState.Loading);
        const result = await accountStore.getStats(accountId);

        setAccountStats(result);
      } catch (e) {
        console.error(`Failed getting overview`, e);
      } finally {
        updateLoadingState(LoadingState.Loaded);
      }
    };

    init();
  }, [accountId]);

  const isLoading = loadingState === LoadingState.Loading;
  const data = [
    {
      title: t('dashboardTemplates:best_stock'),
      value: accountStats.bestStock?.symbol || '-',
      icon: IconsNames.ChartAverage,
      tooltip: accountStats.bestStock ? (
        <Flex vertical={true}>
          <Statistic
            prefix={`${t('common:pnl')} - $`}
            value={accountStats.bestStock?.profitAndLoss}
          />
          <TextComponent level={7}>
            {t('common:date')} - {formatDate(accountStats.bestStock?.day)}
          </TextComponent>
        </Flex>
      ) : null,
    },
    {
      title: t('dashboardTemplates:best_day'),
      value: accountStats.bestDay?.day || '-',
      icon: IconsNames.Exchange,
      tooltip: accountStats.bestDay ? (
        <Statistic prefix={'P&L - $'} value={accountStats.bestDay.total} />
      ) : null,
    },
    {
      title: t('dashboardTemplates:risk_reward_ratio'),
      value: accountStats.riskRewardRatio
        ? '1 : ' + accountStats.riskRewardRatio
        : '-',
      icon: IconsNames.InvoiceOutline,
    },
    {
      title: t('dashboardTemplates:success_rate'),
      value: `${((accountStats.successRate ?? 0) * 100).toFixed(2)}%`,
      icon: IconsNames.CheckTwo,
    },
  ];

  return (
    <Row gutter={isMobile ? [16, 16] : [24, 24]}>
      {data.map(({ title, value, icon, tooltip }) => (
        <Col key={title} xxl={6} xs={12}>
          <Card
            hoverable={false}
            borderLess
            className={classNames('best-rates-cards__card', {
              'best-rates-cards__card--mobile': isMobile,
            })}
          >
            <Tooltip title={tooltip}>
              <Flex align="center" gap={10}>
                <Flex
                  align="center"
                  justify="center"
                  className="best-rates-cards__card-icon-box"
                >
                  <Icons
                    className="best-rates-cards__card-icon"
                    iconName={icon}
                  />
                </Flex>

                <Flex vertical align={'flex-start'} gap={isMobile ? 0 : 5}>
                  <TextComponent weight={600} level={6}>
                    {title}
                  </TextComponent>
                  <TextComponent
                    loading={isLoading}
                    weight={600}
                    level={isMobile ? 6 : 4}
                  >
                    {value}
                  </TextComponent>
                </Flex>
              </Flex>
            </Tooltip>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default BestRatesCards;
